import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { SLIDES, FEATURES, QUICK_LINKS, COUNTERS, GALLERY_IMGS } from "../data/siteData";
import { NotificationBar, SectionTitle } from "../components/Shared";
import Icon from "../components/Icon";
import aboutWhyChoose from "../assets/images/about/whychhose.webp";
import "./HomePage.css";

const HomePage = () => {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setSlide((prev) => (prev + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      {/* ─── Hero Slider ──────────────────────────────────────── */}
      <section className="hero">
        {SLIDES.map((s, i) => (
          <div key={i} className={`hero__slide ${i === slide ? "hero__slide--active" : ""}`}>
            <img src={s.img} alt={s.title} className="hero__img" />
            <div className="hero__overlay" />
          </div>
        ))}
        <div className="hero__content">
          <p className="hero__eyebrow">Welcome to IDPS Mahore</p>
          <h2 className="hero__title">
            {SLIDES[slide].title}{" "}
            <span className="hero__highlight">{SLIDES[slide].highlight}</span>
          </h2>
          <p className="hero__desc">
            A co-educational CBSE school committed to holistic development — where
            children blossom into fine young individuals.
          </p>
          <div className="hero__btns">
            <Link to="/admissions" className="btn btn--accent">Admission 2026-27</Link>
            <Link to="/academics" className="btn btn--outline">Explore Academics</Link>
          </div>
          <div className="hero__dots">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                className={`hero__dot ${i === slide ? "hero__dot--active" : ""}`}
                onClick={() => setSlide(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <NotificationBar />

      {/* ─── Features ─────────────────────────────────────────── */}
      <section className="section section--bg">
        <div className="container">
          <SectionTitle
            title="Why IDPS Mahore?"
            subtitle="Providing quality education with holistic development in a nurturing environment."
          />
          <div className="grid-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="feature-card card">
                <div className="feature-card__icon"><Icon name={f.icon} size={26} /></div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── About Preview ────────────────────────────────────── */}
      <section className="section">
        <div className="container grid-2">
          <div className="about-preview__img-wrap">
            <img
              src={aboutWhyChoose}
              alt="About IDPS Mahore"
              className="about-preview__img"
            />
          </div>
          <div className="about-preview__text">
            <span className="about-preview__eyebrow">About Our School</span>
            <h2>Welcome to IDPS Mahore</h2>
            <p>
              The IDPS Mahore is a co-educational CBSE school — a collaboration
              between The International Delhi Public School Society (IDPSS), New
              Delhi, and a local initiative in Mahore, J&K. We ensure education
              with a holistic perspective where every child's intellectual,
              emotional, social, physical, creative, and spiritual potential is
              nurtured.
            </p>
            <p>
              Our students are prepared not only to score marks but to become the
              kind of human beings that can contribute to making this world a
              better place.
            </p>
            <ul className="about-preview__list">
              {[
                "Best Learning Environment in Mahore",
                "Indoor & Outdoor Activities",
                "Highly Qualified Teachers",
                "Focus on Creativity, Leadership & Teamwork",
              ].map((t) => (
                <li key={t}>
                  <span className="about-preview__check"><CheckCircle2 size={16} /></span> {t}
                </li>
              ))}
            </ul>
            <Link to="/about" className="btn btn--accent">Read More</Link>
          </div>
        </div>
      </section>

      {/* ─── Discover More ────────────────────────────────────── */}
      <section className="section section--bg">
        <div className="container">
          <SectionTitle
            title="Discover More"
            subtitle="Explore everything IDPS Mahore has to offer."
          />
          <div className="discover-grid">
            {QUICK_LINKS.map((q) => (
              <Link key={q.label} to={q.path} className="discover-card card">
                <div className="discover-card__icon"><Icon name={q.icon} size={28} color="#fff" /></div>
                <div className="discover-card__label">{q.label}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Counters ─────────────────────────────────────────── */}
      <section className="counter-section">
        <div className="container counter-grid">
          {COUNTERS.map((c) => (
            <div key={c.label} className="counter-item">
              <div className="counter-item__icon"><Icon name={c.icon} size={30} color="#fff" /></div>
              <h3>{c.num}</h3>
              <p>{c.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Gallery Preview ──────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <SectionTitle title="School Gallery" subtitle="Glimpses of life at IDPS Mahore" />
          <div className="gallery-preview-grid">
            {GALLERY_IMGS.map((img, i) => (
              <div key={i} className="gallery-preview__item">
                <img src={img} alt={`Gallery ${i + 1}`} />
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 28 }}>
            <Link to="/gallery" className="btn btn--outline-dark">
              View Full Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Admission CTA ────────────────────────────────────── */}
      <section className="cta-section">
        <div className="container" style={{ textAlign: "center" }}>
          <h2>Admissions Open 2026-27</h2>
          <p>
            We welcome you and share our enthusiasm as we open the gates of IDPS
            Mahore. Committed to high expectations for all students.
          </p>
          <div className="cta-section__btns">
            <Link to="/contact" className="btn btn--accent">Enquire Now</Link>
            <Link to="/about" className="btn btn--outline">Learn More</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
